import React from 'react';
import { Link, useParams } from 'react-router-dom';
import ROUTES from '../routes.json';
import { normalizeRoute } from '../utils/routeHelpers';
import posts from '../content/posts.json';
import { Helmet } from 'react-helmet-async';
import PageHelmet from '../components/PageHelmet';

export default function BlogPage({ slug: propSlug }) {
  const allPosts = posts;
  const params = useParams();
  const slug = propSlug || params.slug;

  // Find the post that matches the URL slug. If none is found, selected will be undefined
  const selected = allPosts.find(p => p.slug === slug);

  // Build a canonical URL for the page (used by PageHelmet). When a post is
  // selected we point to the post canonical; otherwise the blog index canonical.
  const pageCanonical = (selected ? (typeof window !== 'undefined' ? window.location.origin + normalizeRoute(`/blog/${selected.slug}/`) : `/blog/${selected.slug}/`) : (typeof window !== 'undefined' ? window.location.origin + normalizeRoute(ROUTES.blog.path) : ROUTES.blog.path));

  // Head for posts is rendered inline with Helmet in the article markup below

  return (
    <>
      {/* Page-level Helmet: use PageHelmet so meta description/title are unique and
          controlled by React Helmet (avoid duplicate default descriptions). */}
      <PageHelmet
        title={selected ? `${selected.title} — OneAI` : ROUTES.blog.title}
        description={selected ? (selected.description || selected.excerpt) : ROUTES.blog.description}
        canonical={pageCanonical}
        image={selected ? selected.image : undefined}
        type={selected ? 'article' : 'website'}
      />
      <section className="tool-area">
      <div className="tool-controls">
        <div className="usage">Blog — Insights & updates</div>
        {/* If no post is selected, render the page-level H1 here. When a post is selected,
            the H1 for that post is rendered inside the article so there is exactly one H1 */}
        {!selected && (
          <h1 className="page-title" style={{ margin: '8px 0' }}>Blog — Insights & updates</h1>
        )}
        {/* Add internal links from the blog index to key site routes to reduce orphan/canonical-without-inlinks issues */}
        {!selected && (
          <div style={{ marginTop: 8 }}>
            <strong>Explore:</strong>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              <Link to={normalizeRoute(ROUTES.humanizer.path)} className="btn ghost" style={{ padding: '6px 10px' }}>Humanizer</Link>
              <Link to={normalizeRoute(ROUTES.blogBuilder.path)} className="btn ghost" style={{ padding: '6px 10px' }}>Blog Builder</Link>
              <Link to={normalizeRoute(ROUTES.aiNews.path)} className="btn ghost" style={{ padding: '6px 10px' }}>AI News</Link>
              <Link to={normalizeRoute(ROUTES.tagGenerator.path)} className="btn ghost" style={{ padding: '6px 10px' }}>Meta Tag Generator</Link>
              <Link to={normalizeRoute(ROUTES.contact.path)} className="btn ghost" style={{ padding: '6px 10px' }}>Contact</Link>
              <Link to={normalizeRoute(ROUTES.about.path)} className="btn ghost" style={{ padding: '6px 10px' }}>About</Link>
            </div>
          </div>
        )}
      </div>

      <div className="tool-grid">
        <div className="panel panel-left" aria-label="Posts list">
          <h3 style={{ marginTop: 0 }}>Recent Posts</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            {allPosts.map(post => (
                <Link
                key={post.id}
                to={normalizeRoute(`/blog/${post.slug}/`)}
                className={`list-link ${selected?.slug === post.slug ? 'active' : ''}`}
                style={{ textAlign: 'left' }}
              >
                <div style={{ fontWeight: 700 }}>{post.title}</div>
                {post.image && (
  (() => {
    const ensure = (u) => {
      if (!u) return u;
      if (/^https?:\/\//i.test(u)) return u;
      return u.startsWith('/') ? u : ('/' + u);
    };
    // Special-case: use the new canonical image filename for the recently added post.
    let imgUrl = ensure(post.image);
    if (post && post.slug === 'how-to-make-ai-writing-sound-human-10-techniques-2025') {
      // prefer the JPG variant here (site will also provide a .webp source via <picture>)
      imgUrl = '/images/human-10-proven-techniques.jpg';
    }
    return (
      <picture>
        <source type="image/webp" srcSet={String(imgUrl).replace(/\.(png|jpe?g)$/i, '.webp')} />
        <img
          src={imgUrl}
          alt={post.imageAlt || "Blog Image"}
          loading="lazy"
          style={{
            width: "100%",
            borderRadius: "10px",
            marginTop: "10px",
            marginBottom: "15px",
          }}
        />
      </picture>
    );
  })()
)}

                <div style={{ fontSize: 13, color: '#6b7280' }}>{post.excerpt}</div>
                </Link>
            ))}
          </div>
        </div>

        <div className="panel panel-right" aria-live="polite">
          <div className="panel-header" style={{ marginBottom: 8 }}>
            {/* Keep a small header area; the H1 for a selected post is inside the article below.
                When no post is selected we already render the page H1 above. */}
            <div style={{ margin: 0, fontSize: 20 }}>{selected ? '' : 'Select a post'}</div>
          </div>

          {/* NEW: Hero image */}
          {selected?.image && (
            (() => {
              const ensure = (u) => {
                if (!u) return u;
                if (/^https?:\/\//i.test(u)) return u;
                return u.startsWith('/') ? u : ('/' + u);
              };
              let imgUrl = ensure(selected.image);
              if (selected && selected.slug === 'how-to-make-ai-writing-sound-human-10-techniques-2025') {
                imgUrl = '/images/human-10-proven-techniques.jpg';
              }
              return (
                <div className="post-hero">
                  <picture>
                    <source type="image/webp" srcSet={String(imgUrl).replace(/\.(png|jpe?g)$/i, '.webp')} />
                    <img src={imgUrl} alt={selected.imageAlt || selected.title} className="hero-image" loading="lazy" />
                  </picture>
                  {selected.description && <div className="hero-caption">{selected.description}</div>}
                </div>
              );
            })()
          )}

          {/* Post meta */}
          <div className="post-meta">{selected?.author} • {selected?.publishDate}</div>

          <div className="output-area" style={{ padding: 16 }}>
            {selected ? (
                <article>
                  {(() => {
                    // Build a normalized canonical for the post (ensure trailing slash)
                    const buildCanonical = (p) => {
                      try {
                        const u = new URL(p, window.location.origin);
                        const seg = u.pathname.split('/').filter(Boolean).slice(-1)[0] || '';
                        if (!u.pathname.endsWith('/') && !seg.includes('.')) u.pathname = u.pathname + '/';
                        return u.toString();
                      } catch (e) {
                        return p;
                      }
                    };

                    const postBase = window.location.origin + normalizeRoute(`/blog/${selected.slug}/`);
                    const canonical = buildCanonical(postBase);
                    const jsonld = {
                      "@context": "https://schema.org",
                      "@type": "Article",
                      "mainEntityOfPage": { "@type": "WebPage", "@id": canonical },
                      "headline": selected.title,
                      "image": [selected.image],
                      "datePublished": selected.publishDate,
                      "author": { "@type": "Person", "name": selected.author || "OneAI Editorial" },
                      "description": selected.description || selected.excerpt,
                      "publisher": { "@type": "Organization", "name": "OneAI", "url": window.location.origin },
                      ...(Array.isArray(selected.faqs) && selected.faqs.length ? { mainEntity: selected.faqs.map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) } : {})
                    };

                    return (
                      <Helmet>
                        <title>{`${selected.title} — OneAI`}</title>
                        <link rel="canonical" href={canonical} />
                        <script type="application/ld+json">{JSON.stringify(jsonld)}</script>
                      </Helmet>
                    );
                  })()}
                  {/* Render a single H1 for the selected post. Use a custom H1 for the
                      /blog/ai-humanizer path if requested; otherwise use the post title. */}
                  {selected && (
                    <h1 style={{ margin: 0, fontSize: 24 }}>
                      {slug === 'ai-humanizer'
                        ? 'AI Text Humanizer: Turn AI Text into Human-Like Content'
                        : selected.title}
                    </h1>
                  )}


                {/* Render either legacy HTML content (string) or the new structured JSON sections */}
                {Array.isArray(selected.content) ? (
                  <div className="structured-content" style={{ marginTop: 12 }}>
                    {/* Build a small slugify helper for anchors */}
                    {(() => {
                      const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

                      // Table of contents from level 2/3 headings
                      const headings = selected.content.filter(sec => sec.heading && (sec.level === 2 || sec.level === 3));
                      if (headings.length) {
                        return (
                          <nav className="post-toc" aria-label="Table of contents" style={{ marginBottom: 12 }}>
                            <strong style={{ display: 'block', marginBottom: 6 }}>On this page</strong>
                            <ul style={{ margin: 0, paddingLeft: 14 }}>
                              {headings.map((h, i) => (
                                <li key={i} style={{ marginLeft: h.level === 3 ? 12 : 0 }}>
                                  <a href={`#${slugify(h.heading)}`}>{h.heading}</a>
                                </li>
                              ))}
                            </ul>
                          </nav>
                        );
                      }
                      return null;
                    })()}

                    {(() => {
                      const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

                      return selected.content.map((sec, idx) => {
                        const key = idx;
                        const id = sec.heading ? slugify(sec.heading) : `section-${idx}`;

                        if (sec.image) {
                          const ensure = (u) => {
                            if (!u) return u;
                            if (/^https?:\/\//i.test(u)) return u;
                            return u.startsWith('/') ? u : ('/' + u);
                          };
                          const imgUrl = ensure(sec.image);
                          return (
                            <div key={key} id={id} style={{ marginTop: 12 }}>
                              <picture>
                                <source type="image/webp" srcSet={String(imgUrl).replace(/\.(png|jpe?g)$/i, '.webp')} />
                                <img src={imgUrl} alt={sec.imageAlt || ''} style={{ width: '100%', borderRadius: 8 }} loading="lazy" />
                              </picture>
                              {sec.caption && <div style={{ color: '#6b7280', marginTop: 6 }}>{sec.caption}</div>}
                            </div>
                          );
                        }

                        return (
                          <section key={key} id={id} style={{ marginTop: 12 }}>
                            {sec.heading && (sec.level === 3 ? <h3>{sec.heading}</h3> : <h2>{sec.heading}</h2>)}
                            {sec.content && (Array.isArray(sec.content) ? (
                              sec.content.map((p, i) => <p key={i}>{p}</p>)
                            ) : (
                              // split on double newlines to get paragraphs if a single string is provided
                              String(sec.content).split(/\n\n+/).map((p, i) => <p key={i}>{p.trim()}</p>)
                            ))}
                          </section>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  (() => {
                    const normalizeHtmlPaths = (html) => {
                      if (!html) return html || '';
                      let t = String(html).replace(/\n/g, '');
                      try {
                        // Normalize src attributes: add leading slash if not absolute
                        t = t.replace(/(src\s*=\s*\")([^\"']+)/gi, (m, p1, p2) => {
                          const v = p2.trim();
                          if (!v) return m;
                          if (/^\/?https?:\/\//i.test(v) || v.startsWith('/')) return p1 + v;
                          return p1 + '/' + v;
                        });

                        // Normalize srcset attributes: each entry may be 'url 1x, url2 2x'
                        t = t.replace(/(srcset\s*=\s*\")([^\"]+)\"/gi, (m, p1, p2) => {
                          const parts = p2.split(',').map(s => s.trim()).filter(Boolean).map(entry => {
                            const [url, descriptor] = entry.split(/\s+/);
                            const rest = descriptor ? (' ' + descriptor) : '';
                            if (!url) return entry;
                            if (/^(\/|https?:\/\/)/i.test(url)) return url + rest;
                            return '/' + url + rest;
                          });
                          return p1 + parts.join(', ') + '"';
                        });
                      } catch (e) {
                        // fallback: return original
                        return String(html).replace(/\n/g, '');
                      }
                      return t;
                    };

                    const safeHtml = normalizeHtmlPaths(selected.content);
                    return <div style={{ marginTop: 12 }} dangerouslySetInnerHTML={{ __html: safeHtml }} />;
                  })()
                )}

                {/* Internal links */}
                {/* Related / Internal links: prefer explicit selected.internalLinks, otherwise auto-select related posts.
                    Always render a short outgoing-links block with at least two internal links (fallbacks included)
                */}
                {(() => {
                  const related = [];
                  if (selected) {
                    if (Array.isArray(selected.internalLinks) && selected.internalLinks.length) {
                      selected.internalLinks.forEach(slug => {
                        const p = allPosts.find(pp => pp.slug === slug);
                        if (p) related.push(p);
                      });
                    } else {
                      // pick up to 3 other posts as related (simple heuristic: neighbor posts)
                      const others = allPosts.filter(p => p.slug !== selected.slug);
                      for (let i = 0; i < Math.min(3, others.length); i++) related.push(others[i]);
                    }
                  }

                  // Build outgoing link items: prefer related post links, then fall back to key site pages
                  const outgoing = [];
                  related.forEach(p => outgoing.push({ title: p.title, to: normalizeRoute(`/blog/${p.slug}/`) }));

                  // Ensure we have at least 2 internal links visible to crawlers
                  const ensureFallback = (path, title) => {
                    if (!outgoing.find(o => o.to === path)) outgoing.push({ title, to: path });
                  };

                  ensureFallback(normalizeRoute(ROUTES.blog.path), 'Blog Home');
                  ensureFallback(normalizeRoute(ROUTES.humanizer.path), 'Try the Humanizer');

                  // Trim to max 3 links for compactness
                  const visible = outgoing.slice(0, 3);

                  return (
                    <div style={{ marginTop: 20 }}>
                      <h4>Further reading</h4>
                      <ul>
                        {visible.map((o, idx) => (
                          <li key={idx}><Link to={o.to}>{o.title}</Link></li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}

                {/* FAQ */}
                {selected.faqs && selected.faqs.length > 0 && (
                  <section style={{ marginTop: 18 }}>
                    <h3>Frequently Asked Questions</h3>
                    {selected.faqs.map((f, i) => (
                      <details key={i} style={{ marginBottom: 8 }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{f.q}</summary>
                        <p style={{ marginTop: 8, color: '#6b7280' }}>{f.a}</p>
                      </details>
                    ))}
                  </section>
                )}

                <div style={{ marginTop: 18 }}>
                  <Link to={normalizeRoute(ROUTES.humanizer.path)} className="btn ghost">Try the Humanizer — humanize your draft</Link>
                </div>
                {/* 🔗 External Outgoing Link (Ahrefs “No outgoing links” FIX) */}
<div style={{ marginTop: 25 }}>
  <p style={{ color: '#6b7280' }}>
    Explore more AI breakthroughs at{' '}
    <a 
      href="https://openai.com" 
      target="_blank" 
      rel="noopener noreferrer"
      style={{ color: '#4f46e5', fontWeight: '600' }}
    >
      OpenAI
    </a>.
  </p>
</div>

              </article>
            ) : (
              <div className="placeholder">Choose a post from the left to read it here.</div>
            )}
          </div>
        </div>
      </div>
    </section>
    </>
  );
}
